import { IArena } from "@/redux/rent/types";
import { formatArenaName, sortArenas } from "./arenaNames";

interface BitrixActiveCourt {
  courtId: string;
  courtName: string;
  courtType: string;
  courtRentVAT: number;
  racketServiceId?: string;
  racketServiceName?: string;
  racketServicePrice?: number;
  racketServiceVAT?: number;
}

interface BitrixRentInfo {
  busy: boolean;
  busyWithYou: boolean;
  finishTime: string;
  price: number;
  startTime: string;
}

interface BitrixScheduleItem {
  courtId: string;
  rentInfo: BitrixRentInfo[];
}

interface BitrixScheduleResponse {
  schedule?: BitrixScheduleItem[];
  activeCourts?: BitrixActiveCourt[];
}

const COURT_TYPE_COLORS: { [key: string]: string } = {
  "Хард, первый корпус": "#3D981C",
  "Хард, второй корпус": "#2B8956",
  "Одиночный": "#2A4CC6",
  "Стенка": "#2A84C6",
  "Padel": "#1EB2A6",
  "Padel (улица)": "#0EA5E9",
  "Залы": "#E03F72",
  "Грунт": "#6A4D13",
};

function getCourtTypeColor(courtType: string): string {
  if (COURT_TYPE_COLORS[courtType]) return COURT_TYPE_COLORS[courtType];

  const trimmed = courtType.trim();
  if (COURT_TYPE_COLORS[trimmed]) return COURT_TYPE_COLORS[trimmed];

  const lower = trimmed.toLowerCase();

  if (lower.includes("padel")) {
    if (lower.includes("улиц")) return COURT_TYPE_COLORS["Padel (улица)"];
    return COURT_TYPE_COLORS["Padel"];
  }
  if (lower.includes("хард") || lower.includes("hard")) {
    if (lower.includes("второй") || lower.includes("2")) return COURT_TYPE_COLORS["Хард, второй корпус"];
    return COURT_TYPE_COLORS["Хард, первый корпус"];
  }
  if (lower.includes("одиноч")) return COURT_TYPE_COLORS["Одиночный"];
  if (lower.includes("стенк")) return COURT_TYPE_COLORS["Стенка"];
  if (lower.includes("зал")) return COURT_TYPE_COLORS["Залы"];
  if (lower.includes("грунт")) return COURT_TYPE_COLORS["Грунт"];

  return "#CCCCCC";
}

export function transformBitrixDataToArenas(
  scheduleData: BitrixScheduleItem[],
  activeCourts: BitrixActiveCourt[],
  date: string
): IArena[] {
  const activeCourtsMap = new Map<string, BitrixActiveCourt>();
  activeCourts.forEach((court) => {
    activeCourtsMap.set(court.courtId, court);
  });

  const arenasMap = new Map<string, {
    courtType: string;
    courts: Array<{
      courtId: string;
      courtName: string;
      activeCourt: BitrixActiveCourt;
    }>;
    scheduleItems: BitrixScheduleItem[];
  }>();

  scheduleData.forEach((scheduleItem) => {
    const activeCourt = activeCourtsMap.get(scheduleItem.courtId);
    if (!activeCourt) {
      console.warn(`Active court not found for courtId: ${scheduleItem.courtId}`);
      return;
    }

    const courtType = activeCourt.courtType;
    
    if (!arenasMap.has(courtType)) {
      arenasMap.set(courtType, {
        courtType,
        courts: [],
        scheduleItems: [],
      });
    }

    const arena = arenasMap.get(courtType)!;
    
    const existingCourt = arena.courts.find(c => c.courtId === scheduleItem.courtId);
    if (!existingCourt) {
      arena.courts.push({
        courtId: scheduleItem.courtId,
        courtName: activeCourt.courtName,
        activeCourt,
      });
    }
    
    arena.scheduleItems.push(scheduleItem);
  });

  const arenas: IArena[] = Array.from(arenasMap.values()).map((arenaData, index) => {
    const sortedCourts = [...arenaData.courts].sort((a, b) => {
      const numA = parseInt(a.courtName.match(/\d+/)?.[0] || "999999");
      const numB = parseInt(b.courtName.match(/\d+/)?.[0] || "999999");
      
      if (numA !== numB) {
        return numA - numB;
      }
      return 0;
    });

    const courts = sortedCourts.map((court, courtIndex) => {
      const courtIdHash = court.courtId.split('').reduce((acc, char) => {
        return ((acc << 5) - acc) + char.charCodeAt(0);
      }, 0);
      const numericId = Math.abs(courtIdHash) % 1000000;
      
      return {
        id: numericId,
        active: 1,
        arena_id: index + 1,
        cell_color: getCourtTypeColor(arenaData.courtType),
        created_at: new Date().toISOString(),
        sort: courtIndex + 1,
        title: court.courtName,
        updated_at: new Date().toISOString(),
        // Дополнительные поля из activeCourt
        courtId: court.courtId,
        racketServiceId: court.activeCourt.racketServiceId,
        racketServicePrice: court.activeCourt.racketServicePrice,
        racketServiceVAT: court.activeCourt.racketServiceVAT,
      };
    });

    const seats: Array<{
      id: number;
      court_id: number;
      time: string;
      price: number;
      price_weekend: number;
      is_special_price: boolean;
      busy?: boolean;
      busyWithYou?: boolean;
    }> = [];

    const unAvailableSeats: { [key: number]: number[] } = {};
    let seatIdCounter = 1;

    courts.forEach((court) => {
      const scheduleItem = arenaData.scheduleItems.find(
        (item) => (court as any).courtId === item.courtId
      );

      if (!scheduleItem) {
        return;
      }

      const sortedRentInfo = [...scheduleItem.rentInfo].sort((a, b) => {
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      });

      sortedRentInfo.forEach((rentInfo) => {
        const startTime = new Date(rentInfo.startTime);
        const hours = startTime.getHours().toString().padStart(2, '0');
        const minutes = startTime.getMinutes().toString().padStart(2, '0');
        const timeString = `${hours}:${minutes}`;
        
        const dayOfWeek = startTime.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        const price = rentInfo.price;
        const price_weekend = isWeekend ? price : price;
        const busy = rentInfo.busy || false;
        const busyWithYou = rentInfo.busyWithYou || false;

        const seatId = seatIdCounter++;
        seats.push({
          id: seatId,
          court_id: court.id,
          time: timeString,
          price: price,
          price_weekend: price_weekend,
          is_special_price: false,
          busy: busy,
          busyWithYou: busyWithYou,
        });

        if (busy || busyWithYou) {
          if (!unAvailableSeats[court.id]) {
            unAvailableSeats[court.id] = [];
          }
          unAvailableSeats[court.id].push(seatId);
        }
      });
    });

    return {
      id: index + 1,
      title: formatArenaName(arenaData.courtType),
      courts: courts as any,
      seats: seats as any,
      cell_color: getCourtTypeColor(arenaData.courtType),
      unAvailableSeats,
    };
  });

  const sortedArenas = sortArenas(arenas);
  
  return sortedArenas.reverse();
}

export function addExtraTimeSlots(arenas: IArena[]): IArena[] {
  return arenas.map((arena) => {
    // Проверяем, содержит ли название "Грунт"
    if (!arena.title.toLowerCase().includes("грунт")) {
      return arena;
    }

    const updatedSeats = [...arena.seats];
    const updatedUnAvailableSeats = { ...arena.unAvailableSeats };
    let maxSeatId = Math.max(...updatedSeats.map(s => s.id), 0);

    // Обрабатываем каждый корт в арене
    arena.courts.forEach((court) => {
      // Добавляем слот 07:00-08:00 для каждого корта
      const has0700 = updatedSeats.some(
        (seat) => seat.time === "07:00" && seat.court_id === court.id
      );
      
      if (!has0700) {
        maxSeatId++;
        const slot0700 = {
          id: maxSeatId,
          court_id: court.id,
          time: "07:00",
          price: 2000,
          price_weekend: 2000,
          is_special_price: false,
          busy: true,
        };
        updatedSeats.push(slot0700 as any);
        if (!updatedUnAvailableSeats[court.id]) {
          updatedUnAvailableSeats[court.id] = [];
        }
        updatedUnAvailableSeats[court.id].push(slot0700.id);
      } else {
        // Помечаем существующий слот как busy
        updatedSeats.forEach((seat) => {
          if (seat.time === "07:00" && seat.court_id === court.id) {
            (seat as any).busy = true;
            if (!updatedUnAvailableSeats[court.id]) {
              updatedUnAvailableSeats[court.id] = [];
            }
            if (!updatedUnAvailableSeats[court.id].includes(seat.id)) {
              updatedUnAvailableSeats[court.id].push(seat.id);
            }
          }
        });
      }

      // Добавляем слот 23:00-24:00 для каждого корта
      const has2300 = updatedSeats.some(
        (seat) => seat.time === "23:00" && seat.court_id === court.id
      );
      
      if (!has2300) {
        maxSeatId++;
        const slot2300 = {
          id: maxSeatId,
          court_id: court.id,
          time: "23:00",
          price: 2000,
          price_weekend: 2000,
          is_special_price: false,
          busy: true,
        };
        updatedSeats.push(slot2300 as any);
        if (!updatedUnAvailableSeats[court.id]) {
          updatedUnAvailableSeats[court.id] = [];
        }
        updatedUnAvailableSeats[court.id].push(slot2300.id);
      } else {
        // Помечаем существующий слот как busy
        updatedSeats.forEach((seat) => {
          if (seat.time === "23:00" && seat.court_id === court.id) {
            (seat as any).busy = true;
            if (!updatedUnAvailableSeats[court.id]) {
              updatedUnAvailableSeats[court.id] = [];
            }
            if (!updatedUnAvailableSeats[court.id].includes(seat.id)) {
              updatedUnAvailableSeats[court.id].push(seat.id);
            }
          }
        });
      }
    });

    return {
      ...arena,
      seats: updatedSeats,
      unAvailableSeats: updatedUnAvailableSeats,
    };
  });
}
